// HostelOpsPages.jsx — Hostel Member's operational pages: rooms, allocation, fines.
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon,
  ArrowUturnLeftIcon, HomeModernIcon, ClockIcon,
} from '@heroicons/react/24/outline';

const ACCENT = '#ec4899';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .hp * { box-sizing:border-box; }
  .hp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:hpFade .4s ease both; }
  @keyframes hpFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .hp-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .hp-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .hp-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-accent { background:${ACCENT}; color:white; }
  .btn-accent:hover { filter:brightness(0.94); box-shadow:0 4px 14px ${ACCENT}55; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .tab  { padding:6px 14px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.75rem; font-weight:600; cursor:pointer; color:#6b7194; }
  .tab.active { background:${ACCENT}; color:white; border-color:${ACCENT}; }
  .toolbar { display:flex; gap:.6rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .search-wrap { position:relative; flex:1; max-width:320px; }
  .search-wrap svg { position:absolute; left:11px; top:50%; transform:translateY(-50%); width:15px; height:15px; stroke:#9094a8; }
  .search-inp { font-family:'Sora',sans-serif; font-size:.8rem; width:100%; padding:8px 12px 8px 32px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; outline:none; }
  .search-inp:focus { border-color:${ACCENT}; background:white; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 14px; text-align:center; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; }
  .tbl td { padding:10px 14px; font-size:.8rem; text-align:center; vertical-align:middle; border-bottom:1px solid #f3f5fb; }
  .tbl tbody tr:hover { background:#fafbff; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:180px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:${ACCENT}; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .pill { font-size:.66rem; font-weight:600; padding:3px 9px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .pill-active  { background:#ecfdf5; color:#10c98e; }
  .pill-vacated { background:#f0f2f7; color:#9094a8; }
  .pill-maintenance { background:#fffbeb; color:#f59e0b; }
  .pill-inactive { background:#fef2f2; color:#ef4444; }
  .pill-pending { background:#fffbeb; color:#f59e0b; }
  .pill-applied { background:#ecfdf5; color:#10c98e; }
  .pill-in  { background:#ecfdf5; color:#10c98e; }
  .pill-out { background:#fef2f2; color:#ef4444; }
  .row-actions { display:inline-flex; gap:6px; align-items:center; flex-wrap:wrap; justify-content:center; }
  .modal.wide { max-width:640px; }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
  .modal-head { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f2f8; }
  .modal-title { font-size:.95rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-body { padding:18px 20px; }
  .modal-foot { display:flex; gap:8px; justify-content:flex-end; padding:14px 20px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.full { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:${ACCENT}; background:white; }
  .suggest-list { border:1.5px solid #e0e4f0; border-radius:9px; margin-top:4px; max-height:160px; overflow-y:auto; background:white; }
  .suggest-item { padding:8px 11px; font-size:.8rem; cursor:pointer; border-bottom:1px solid #f3f5fb; }
  .suggest-item:hover { background:#fdf2f8; }
  .picked-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:9px; background:#fdf2f8; border:1px solid #fbcfe8; font-size:.78rem; font-weight:600; color:#9d174d; }
  .room-details { display:flex; flex-wrap:wrap; gap:6px; margin-top:6px; font-size:.72rem; color:#6b7194; }
  .dd-trigger { display:flex; align-items:center; gap:8px; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; cursor:pointer; font-size:.82rem; }
  .dd-trigger:hover { border-color:${ACCENT}; }
  .dd-panel { position:absolute; z-index:20; top:calc(100% + 4px); left:0; right:0; background:white; border:1.5px solid #e0e4f0; border-radius:12px; padding:10px; box-shadow:0 12px 32px rgba(15,17,35,.14); }
  .dd-hint { padding:10px 4px; font-size:.76rem; color:#9094a8; text-align:center; }
  @media (max-width:768px) { .form-grid { grid-template-columns:1fr; } .form-field.full { grid-column:1; } }
`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '—';
const FINE_PRESETS = ['Room Damage', 'Discipline Fine', 'Lost Key', 'Other'];
const UNALLOCATED_ONLY = { unallocatedOnly: 'true' };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const todayStr = () => new Date().toISOString().slice(0,10);
// "Grade 10 — A" (dash skipped when there is no section)
const classLabel = (name, section) => (name && section) ? `${name} — ${section}` : (name || section || '—');
// "2026-09-12 16:54:00" -> "12 Sep 2026, 4:54 PM" (parsed by hand: no timezone shift)
const fmtDateTime = (v) => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return fmtDate(v);
  const h = Number(m[4]);
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}, ${h % 12 || 12}:${m[5]} ${h >= 12 ? 'PM' : 'AM'}`;
};
// Stay date+time from the event-log datetime, falling back to the plain date.
const fmtStay = (at, date) => at ? fmtDateTime(at) : fmtDate(date);
const EventPill = ({ action }) => (
  action === 'check_in' ? <span className="pill pill-in">Check-In</span> : <span className="pill pill-out">Check-Out</span>
);

// ── Closes a dropdown panel when clicking outside its wrapper. Uses the
// 'click' event rather than 'mousedown': dragging a scrollbar (e.g. the
// modal's own overflow-y scrollbar) fires mousedown+mouseup on the scrollable
// container but never a 'click', and picking an option from a native <select>
// fires mousedown while its native popup is open but the resulting 'click'
// lands back on the <select> itself (which is inside `ref`) — so 'mousedown'
// would close the panel on both of those interactions, 'click' does not.
const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'OPTION' || e.target.tagName === 'SELECT') return;
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [ref, onOutside]);
};

// ── Student picker: class + section dropdowns narrow the roster, plus a
// search bar to find anyone directly when there are too many to scroll ─────
const StudentPicker = ({ apiBase, extraParams, onChange }) => {
  const [open, setOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [section, setSection] = useState('');
  const [q, setQ] = useState('');
  const [students, setStudents] = useState([]);
  const [picked, setPicked] = useState(null);
  const wrapRef = useRef(null);
  useClickOutside(wrapRef, () => setOpen(false));

  useEffect(() => { api.get(`${apiBase}/classes`).then(r => setClasses(r.data||[])).catch(()=>{}); }, [apiBase]);

  // Students are listed only once a class AND section are chosen (class alone
  // is enough when it has no sections), or when the user types a search.
  const selectedClass = classes.find(c => String(c._id) === String(classId));
  const needsSection = !!selectedClass?.sections?.length;
  const canList = !!q.trim() || (!!classId && (!needsSection || !!section));

  useEffect(() => {
    if (!open) return;
    if (!canList) { setStudents([]); return; }
    const t = setTimeout(() => {
      const params = new URLSearchParams({ ...(extraParams||{}) });
      if (classId) params.set('classId', classId);
      if (section) params.set('section', section);
      if (q.trim()) params.set('search', q.trim());
      api.get(`${apiBase}/students?${params}`).then(r => setStudents(r.data||[])).catch(()=>{});
    }, 200);
    return () => clearTimeout(t);
  }, [open, canList, classId, section, q, apiBase, extraParams]);

  const pick = (s) => { setPicked(s); onChange(s._id); setOpen(false); setQ(''); };
  const clear = () => { setPicked(null); onChange(null); };

  if (picked) {
    return (
      <div className="picked-chip">
        {picked.name} — {classLabel(picked.className, picked.section)} · {picked.admissionNo}
        <button type="button" onClick={clear} style={{border:'none',background:'none',cursor:'pointer',color:'#9d174d'}}><XMarkIcon style={{width:14,height:14}}/></button>
      </div>
    );
  }
  return (
    <div ref={wrapRef} style={{position:'relative'}}>
      <div className="dd-trigger" onClick={() => setOpen(o => !o)}>
        <MagnifyingGlassIcon style={{width:14,height:14,stroke:'#9094a8',flexShrink:0}}/>
        <span style={{color:'#9094a8'}}>Select a student…</span>
      </div>
      {open && (
        <div className="dd-panel">
          <div style={{display:'flex',gap:6,marginBottom:8}}>
            <select className="form-select" style={{flex:1}} value={classId} onChange={e=>{setClassId(e.target.value); setSection('');}}>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <select className="form-select" style={{flex:1}} value={section} onChange={e=>setSection(e.target.value)} disabled={!needsSection}>
              <option value="">Select Section</option>
              {(selectedClass?.sections||[]).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input autoFocus className="form-input" placeholder="Search by name or admission no…" value={q} onChange={e=>setQ(e.target.value)} />
          <div className="suggest-list" style={{marginTop:8}}>
            {!canList ? <div className="dd-hint">{classId && needsSection ? 'Select a section to see students' : 'Pick a class and section, or search by name to see students'}</div>
            : students.length === 0 ? <div className="dd-hint">No students found</div>
            : students.map(s => (
              <div key={s._id} className="suggest-item" onClick={() => pick(s)}>
                {s.name} — {classLabel(s.className, s.section)} · {s.admissionNo}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Room picker: click to open a dropdown of every room, narrowed by a
// search bar — used on the Fines form so a fine can show real room details ──
const RoomPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const wrapRef = useRef(null);
  useClickOutside(wrapRef, () => setOpen(false));

  useEffect(() => {
    if (open && !loaded) {
      api.get('/hostel_member/rooms').then(r => setAll(r.data||[])).catch(()=>{});
      setLoaded(true);
    }
  }, [open, loaded]);

  if (value === null && picked) setPicked(null);

  const filtered = all.filter(r => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return r.roomNo.toLowerCase().includes(s) || (r.block||'').toLowerCase().includes(s) || (r.floor||'').toLowerCase().includes(s);
  });

  const pick = (r) => { setPicked(r); onChange(r); setOpen(false); setQ(''); };
  const clear = () => { setPicked(null); onChange(null); };

  if (picked) {
    return (
      <div>
        <div className="picked-chip">
          <HomeModernIcon style={{width:14,height:14}}/>Room {picked.roomNo}
          <button type="button" onClick={clear} style={{border:'none',background:'none',cursor:'pointer',color:'#9d174d'}}><XMarkIcon style={{width:14,height:14}}/></button>
        </div>
        {(picked.block || picked.floor || picked.monthlyFee > 0) && (
          <div className="room-details">
            {picked.block && <span>Block {picked.block}</span>}
            {picked.floor && <span>· Floor {picked.floor}</span>}
            <span>· {picked.occupied}/{picked.capacity} occupied</span>
            {picked.monthlyFee > 0 && <span>· Rs. {Number(picked.monthlyFee).toLocaleString()}/mo</span>}
          </div>
        )}
      </div>
    );
  }
  return (
    <div ref={wrapRef} style={{position:'relative'}}>
      <div className="dd-trigger" onClick={() => setOpen(o => !o)}>
        <MagnifyingGlassIcon style={{width:14,height:14,stroke:'#9094a8',flexShrink:0}}/>
        <span style={{color:'#9094a8'}}>Select a room…</span>
      </div>
      {open && (
        <div className="dd-panel">
          <input autoFocus className="form-input" placeholder="Search by room no., block, floor…" value={q} onChange={e=>setQ(e.target.value)} />
          <div className="suggest-list" style={{marginTop:8}}>
            {filtered.length === 0 ? <div className="dd-hint">No rooms found</div>
            : filtered.map(r => (
              <div key={r._id} className="suggest-item" onClick={() => pick(r)}>
                Room {r.roomNo} {r.block ? `— Block ${r.block}` : ''} ({r.occupied}/{r.capacity} occupied)
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Rooms ────────────────────────────────────────────────────────────────────
const EMPTY_ROOM = { roomNo:'', block:'', floor:'', capacity:1, monthlyFee:'', notes:'' };

export const HostelRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ROOM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    api.get(`/hostel_member/rooms${params}`).then(r => setRooms(r.data||[])).catch(()=>toast.error('Failed to load rooms')).finally(()=>setLoading(false));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_ROOM); setModal(true); };
  const openEdit = (r) => { setEditing(r); setForm({ roomNo:r.roomNo, block:r.block, floor:r.floor, capacity:r.capacity, monthlyFee:r.monthlyFee, notes:r.notes||'' }); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.roomNo.trim()) { toast.error('Room number is required'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/hostel_member/rooms/${editing._id}`, form); toast.success('Room updated'); }
      else { await api.post('/hostel_member/rooms', form); toast.success('Room added'); }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete room "${r.roomNo}"?`)) return;
    try { await api.delete(`/hostel_member/rooms/${r._id}`); toast.success('Room deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="hp"><style>{css}</style>
      <div className="hp-header">
        <div><div className="hp-title">Rooms</div><div className="hp-sub">Manage hostel rooms and capacity</div></div>
        <button className="btn btn-accent" onClick={openAdd}><PlusIcon/>Add Room</button>
      </div>
      <div className="toolbar">
        <div className="search-wrap"><MagnifyingGlassIcon/><input className="search-inp" placeholder="Search by room no. or block…" value={search} onChange={e=>setSearch(e.target.value)} /></div>
      </div>
      <div className="panel">
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : rooms.length===0 ? <div className="empty">No rooms found. Click "Add Room" to start.</div>
        : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Room No.</th><th>Block</th><th>Floor</th><th>Occupancy</th><th>Monthly Fee</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {rooms.map(r => (
                  <tr key={r._id}>
                    <td style={{fontWeight:600}}>{r.roomNo}</td>
                    <td>{r.block||'—'}</td>
                    <td>{r.floor||'—'}</td>
                    <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,color:r.occupied>=r.capacity?'#ef4444':'#10c98e'}}>{r.occupied}/{r.capacity}</span></td>
                    <td>{r.monthlyFee > 0 ? `Rs. ${Number(r.monthlyFee).toLocaleString()}` : '—'}</td>
                    <td><span className={`pill pill-${r.status}`}>{r.status}</span></td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(r)}><PencilIcon/></button>
                      <button className="btn btn-red btn-sm" onClick={()=>remove(r)} style={{marginLeft:6}}><TrashIcon/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">{editing?'Edit Room':'Add Room'}</div><button className="modal-close" onClick={()=>setModal(false)}><XMarkIcon/></button></div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-field"><label className="form-label">Room No. *</label><input className="form-input" value={form.roomNo} onChange={e=>setForm(p=>({...p,roomNo:e.target.value}))} required disabled={!!editing}/></div>
                  <div className="form-field"><label className="form-label">Block</label><input className="form-input" value={form.block} onChange={e=>setForm(p=>({...p,block:e.target.value}))}/></div>
                  <div className="form-field"><label className="form-label">Floor</label><input className="form-input" value={form.floor} onChange={e=>setForm(p=>({...p,floor:e.target.value}))}/></div>
                  <div className="form-field"><label className="form-label">Capacity</label><input type="number" min="1" className="form-input" value={form.capacity} onChange={e=>setForm(p=>({...p,capacity:e.target.value}))}/></div>
                  <div className="form-field"><label className="form-label">Monthly Fee (Rs.)</label><input type="number" min="0" className="form-input" value={form.monthlyFee} onChange={e=>setForm(p=>({...p,monthlyFee:e.target.value}))}/></div>
                  {editing && <div className="form-field"><label className="form-label">Status</label>
                    <select className="form-select" value={editing.status} onChange={e=>setEditing(p=>({...p,status:e.target.value}))} onBlur={e=>setForm(p=>({...p,status:e.target.value}))}>
                      <option value="active">Active</option><option value="maintenance">Maintenance</option><option value="inactive">Inactive</option>
                    </select>
                  </div>}
                  <div className="form-field full"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-accent" disabled={saving}>{saving?'Saving…':editing?'Update':'Add Room'}</button>
              </div>
            </form>
          </div>
        </div>, document.body)}
    </div>
  );
};

// ── Full check-in / check-out event log table ───────────────────────────────
const LogTable = ({ rows, forStudent = false }) => (
  <div style={{overflowX:'auto'}}>
    <table className="tbl">
      <thead>
        <tr>
          <th>Date &amp; Time</th>
          {!forStudent && <th>Student</th>}
          {!forStudent && <th>Class</th>}
          {forStudent ? <><th>Event</th><th>Room</th></> : <><th>Room</th><th>Event</th><th>By</th></>}
        </tr>
      </thead>
      <tbody>
        {rows.map(l => (
          <tr key={l._id}>
            <td style={{whiteSpace:'nowrap'}}>{fmtDateTime(l.occurredAt)}</td>
            {!forStudent && <td style={{fontWeight:600}}>{l.studentName}</td>}
            {!forStudent && <td>{classLabel(l.className, l.section)}</td>}
            {forStudent
              ? <><td><EventPill action={l.action}/></td><td style={{fontWeight:600}}>{l.roomNo}</td></>
              : <><td style={{fontWeight:600}}>{l.roomNo}</td><td><EventPill action={l.action}/></td><td>{l.performedBy||'—'}</td></>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Allocation ───────────────────────────────────────────────────────────────
const ALLOC_TABS = [
  { key: '', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'logs', label: 'Logs' },
  { key: 'vacated', label: 'Vacated' },
];

export const HostelAllocation = () => {
  const [allocations, setAllocations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [allocModal, setAllocModal] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [pickedRoom, setPickedRoom] = useState('');
  const [pickedStudent, setPickedStudent] = useState(null);
  const [checkInDate, setCheckInDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [reModal, setReModal] = useState(null); // vacated allocation being re-allocated
  const [reRooms, setReRooms] = useState([]);
  const [reRoom, setReRoom] = useState('');
  const [reDate, setReDate] = useState('');
  const [histFor, setHistFor] = useState(null); // allocation whose history is open
  const [histRows, setHistRows] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const isLogs = statusF === 'logs';

  const load = useCallback(() => {
    setLoading(true);
    if (statusF === 'logs') {
      const params = logSearch.trim() ? `?search=${encodeURIComponent(logSearch.trim())}` : '';
      api.get(`/hostel_member/allocation-logs${params}`).then(r => setLogs(r.data||[])).catch(()=>toast.error('Failed to load logs')).finally(()=>setLoading(false));
    } else {
      const params = statusF ? `?status=${statusF}` : '';
      api.get(`/hostel_member/allocations${params}`).then(r => setAllocations(r.data||[])).catch(()=>toast.error('Failed to load allocations')).finally(()=>setLoading(false));
    }
  }, [statusF, logSearch]);
  useEffect(() => {
    const t = setTimeout(load, statusF === 'logs' && logSearch ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, statusF, logSearch]);

  const freeRoomsOf = (list) => (list||[]).filter(rm => rm.occupied < rm.capacity && rm.status==='active');

  const openAlloc = () => {
    setPickedRoom(''); setPickedStudent(null); setCheckInDate('');
    api.get('/hostel_member/rooms').then(r => setRooms(freeRoomsOf(r.data))).catch(()=>{});
    setAllocModal(true);
  };

  const submitAlloc = async (e) => {
    e.preventDefault();
    if (!pickedRoom || !pickedStudent) { toast.error('Pick both a room and a student'); return; }
    setSaving(true);
    try {
      await api.post('/hostel_member/allocations', { roomId: pickedRoom, studentId: pickedStudent, checkInDate: checkInDate||undefined });
      toast.success('Student allocated'); setAllocModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const vacate = async (a) => {
    if (!window.confirm(`Check out ${a.studentName} from room ${a.roomNo}?`)) return;
    try { await api.put(`/hostel_member/allocations/${a._id}/vacate`, {}); toast.success('Checked out'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const openRealloc = (a) => {
    setReModal(a); setReRoom(''); setReDate(todayStr()); setReRooms([]);
    api.get('/hostel_member/rooms').then(r => {
      const free = freeRoomsOf(r.data);
      setReRooms(free);
      // default to the room the student stayed in before, when it has space
      const prev = free.find(rm => String(rm._id) === String(a.roomId)) || free.find(rm => rm.roomNo === a.roomNo);
      if (prev) setReRoom(String(prev._id));
    }).catch(()=>{});
  };

  const submitRealloc = async (e) => {
    e.preventDefault();
    if (!reRoom) { toast.error('Select a room'); return; }
    setSaving(true);
    try {
      await api.post(`/hostel_member/allocations/${reModal._id}/reallocate`, { roomId: reRoom, checkInDate: reDate || undefined });
      toast.success('Student re-allocated'); setReModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const openHistory = (a) => {
    setHistFor(a); setHistRows([]); setHistLoading(true);
    api.get(`/hostel_member/allocation-logs?studentId=${a.studentId}`)
      .then(r => setHistRows(r.data||[])).catch(()=>toast.error('Failed to load history')).finally(()=>setHistLoading(false));
  };

  const showStatus = statusF === '';

  return (
    <div className="hp"><style>{css}</style>
      <div className="hp-header">
        <div><div className="hp-title">Room Allocation</div><div className="hp-sub">Assign and vacate students from hostel rooms</div></div>
        <button className="btn btn-accent" onClick={openAlloc}><PlusIcon/>Allocate Student</button>
      </div>
      <div className="tabs">
        {ALLOC_TABS.map(t => (
          <button key={t.key||'all'} className={`tab ${statusF===t.key?'active':''}`} onClick={()=>setStatusF(t.key)}>{t.label}</button>
        ))}
      </div>
      {isLogs && (
        <div className="toolbar">
          <div className="search-wrap"><MagnifyingGlassIcon/><input className="search-inp" placeholder="Search by student or room…" value={logSearch} onChange={e=>setLogSearch(e.target.value)} /></div>
        </div>
      )}
      <div className="panel">
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : isLogs ? (
          logs.length===0 ? <div className="empty">No check-in or check-out events yet.</div> : <LogTable rows={logs} />
        )
        : allocations.length===0 ? <div className="empty">No records here.</div>
        : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Room</th><th>Student</th><th>Class</th><th>Check-In</th><th>Check-Out</th>{showStatus && <th>Status</th>}<th>Actions</th></tr></thead>
              <tbody>
                {allocations.map(a => (
                  <tr key={a._id}>
                    <td style={{fontWeight:600}}>{a.roomNo}</td>
                    <td>{a.studentName}</td>
                    <td>{classLabel(a.className, a.section)}</td>
                    <td style={{whiteSpace:'nowrap'}}>{fmtStay(a.checkInAt, a.checkInDate)}</td>
                    <td style={{whiteSpace:'nowrap'}}>{a.checkOutAt || a.checkOutDate ? fmtStay(a.checkOutAt, a.checkOutDate) : '—'}</td>
                    {showStatus && <td><span className={`pill pill-${a.status}`}>{a.status}</span></td>}
                    <td>
                      <div className="row-actions">
                        {a.status === 'active' && (
                          <span title={a.canCheckOut === false ? 'Cannot check out on the same day as check-in' : undefined}>
                            <button className="btn btn-accent btn-sm" onClick={()=>vacate(a)} disabled={a.canCheckOut === false}
                              title={a.canCheckOut === false ? 'Cannot check out on the same day as check-in' : undefined}><ArrowUturnLeftIcon/>Check Out</button>
                          </span>
                        )}
                        {a.status === 'vacated' && (
                          <button className="btn btn-accent btn-sm" onClick={()=>openRealloc(a)}><PlusIcon/>Re-Allocate</button>
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={()=>openHistory(a)}><ClockIcon/>History</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {allocModal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setAllocModal(false)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">Allocate Student to a Room</div><button className="modal-close" onClick={()=>setAllocModal(false)}><XMarkIcon/></button></div>
            <form onSubmit={submitAlloc}>
              <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
                <div className="form-field">
                  <label className="form-label">Room</label>
                  <select className="form-select" value={pickedRoom} onChange={e=>setPickedRoom(e.target.value)} required>
                    <option value="">Select a room…</option>
                    {rooms.map(r => <option key={r._id} value={r._id}>{r.roomNo} ({r.occupied}/{r.capacity} occupied)</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Student</label>
                  <StudentPicker apiBase="/hostel_member" extraParams={UNALLOCATED_ONLY} onChange={setPickedStudent} />
                </div>
                <div className="form-field">
                  <label className="form-label">Check-In Date</label>
                  <input type="date" className="form-input" value={checkInDate} onChange={e=>setCheckInDate(e.target.value)} />
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setAllocModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-accent" disabled={saving}>{saving?'Allocating…':'Allocate'}</button>
              </div>
            </form>
          </div>
        </div>, document.body)}

      {reModal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setReModal(null)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">Re-Allocate Student</div><button className="modal-close" onClick={()=>setReModal(null)}><XMarkIcon/></button></div>
            <form onSubmit={submitRealloc}>
              <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
                <div style={{fontSize:'.82rem',color:'#6b7194'}}>
                  <b>{reModal.studentName}</b> ({classLabel(reModal.className, reModal.section)})<br/>
                  Previously in room <b>{reModal.roomNo}</b> — checked out {fmtStay(reModal.checkOutAt, reModal.checkOutDate)}
                </div>
                <div className="form-field">
                  <label className="form-label">Room</label>
                  <select className="form-select" value={reRoom} onChange={e=>setReRoom(e.target.value)} required>
                    <option value="">Select a room…</option>
                    {reRooms.map(r => <option key={r._id} value={r._id}>{r.roomNo} ({r.occupied}/{r.capacity} occupied){r.roomNo === reModal.roomNo ? ' — previous room' : ''}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Check-In Date</label>
                  <input type="date" className="form-input" value={reDate} onChange={e=>setReDate(e.target.value)} />
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setReModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-accent" disabled={saving}>{saving?'Saving…':'Re-Allocate'}</button>
              </div>
            </form>
          </div>
        </div>, document.body)}

      {histFor && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setHistFor(null)}>
          <div className="modal wide">
            <div className="modal-head"><div className="modal-title">Hostel History — {histFor.studentName} ({classLabel(histFor.className, histFor.section)})</div><button className="modal-close" onClick={()=>setHistFor(null)}><XMarkIcon/></button></div>
            {histLoading ? <div className="loading"><div className="spinner"/>Loading…</div>
            : histRows.length===0 ? <div className="empty">No history yet.</div>
            : <LogTable rows={histRows} forStudent />}
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={()=>setHistFor(null)}>Close</button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
};

// ── Fines ────────────────────────────────────────────────────────────────────
export const HostelFines = () => {
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [pickedStudent, setPickedStudent] = useState(null);
  const [pickedRoom, setPickedRoom] = useState(null);
  const [reason, setReason] = useState(FINE_PRESETS[0]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/hostel_member/fines').then(r => setFines(r.data||[])).catch(()=>toast.error('Failed to load fines')).finally(()=>setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setPickedStudent(null); setPickedRoom(null); setReason(FINE_PRESETS[0]); setDescription(''); setAmount(''); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!pickedStudent) { toast.error('Pick a student'); return; }
    if (!amount || Number(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      await api.post('/hostel_member/fines', { studentId: pickedStudent, roomId: pickedRoom?._id || undefined, reason, description, amount: Number(amount) });
      toast.success('Fine added'); setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="hp"><style>{css}</style>
      <div className="hp-header">
        <div><div className="hp-title">Fines</div><div className="hp-sub">Room damage, discipline, etc. — automatically added to the student's next fee bill</div></div>
        <button className="btn btn-accent" onClick={openAdd}><PlusIcon/>Add Fine</button>
      </div>
      <div className="panel">
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : fines.length===0 ? <div className="empty">No fines issued yet.</div>
        : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Student</th><th>Class</th><th>Room</th><th>Reason</th><th>Amount</th><th>Month</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {fines.map(f => (
                  <tr key={f._id}>
                    <td style={{fontWeight:600}}>{f.studentName}</td>
                    <td>{classLabel(f.className, f.section)}</td>
                    <td style={{textAlign:'left'}}>
                      {f.roomNo ? (
                        <>
                          <div style={{fontWeight:600}}>Room {f.roomNo}</div>
                          <div style={{fontSize:'.68rem',color:'#9094a8'}}>
                            {[f.roomBlock ? `Block ${f.roomBlock}` : null, f.roomFloor ? `Floor ${f.roomFloor}` : null, f.roomMonthlyFee > 0 ? `Rs. ${Number(f.roomMonthlyFee).toLocaleString()}/mo` : null].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </>
                      ) : '—'}
                    </td>
                    <td>{f.reason}</td>
                    <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>Rs. {Number(f.amount).toLocaleString()}</td>
                    <td>{f.month}</td>
                    <td><span className={`pill pill-${f.status}`}>{f.status === 'applied' ? 'Applied to bill' : 'Pending next bill'}</span></td>
                    <td>{fmtDate(f.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">Add Fine</div><button className="modal-close" onClick={()=>setModal(false)}><XMarkIcon/></button></div>
            <form onSubmit={submit}>
              <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
                <div className="form-field">
                  <label className="form-label">Student</label>
                  <StudentPicker apiBase="/hostel_member" onChange={setPickedStudent} />
                </div>
                <div className="form-field">
                  <label className="form-label">Room <span style={{fontWeight:400,textTransform:'none',color:'#b0b5cc'}}>(optional — links the fine to a room and shows its details)</span></label>
                  <RoomPicker value={pickedRoom} onChange={setPickedRoom} />
                </div>
                <div className="form-field">
                  <label className="form-label">Reason</label>
                  <select className="form-select" value={reason} onChange={e=>setReason(e.target.value)}>
                    {FINE_PRESETS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" value={description} onChange={e=>setDescription(e.target.value)} placeholder="Optional details…" />
                </div>
                <div className="form-field">
                  <label className="form-label">Amount (Rs.)</label>
                  <input type="number" min="1" className="form-input" value={amount} onChange={e=>setAmount(e.target.value)} required/>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-accent" disabled={saving}>{saving?'Saving…':'Add Fine'}</button>
              </div>
            </form>
          </div>
        </div>, document.body)}
    </div>
  );
};
